// src/utils/firebase.js

import { initializeApp, getApps } from 'firebase/app';
import { getAuth, browserLocalPersistence, setPersistence, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, getDoc, doc } from 'firebase/firestore';
import { getDatabase } from 'firebase/database';
import { getStorage } from 'firebase/storage';
// import { getAnalytics } from 'firebase/analytics'; // Unused - removed
import { getFunctions } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { useState, useEffect } from 'react';
import { installAuthHotfix } from './hotfix.auth';

// Environment variables are loaded from .env file

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Debug: Check if environment variables are loaded
console.log('🔧 Firebase Config Check:', {
  apiKey: firebaseConfig.apiKey ? '✅ Loaded' : '❌ Missing',
  authDomain: firebaseConfig.authDomain ? '✅ Loaded' : '❌ Missing',
  databaseURL: firebaseConfig.databaseURL ? '✅ Loaded' : '❌ Missing',
  projectId: firebaseConfig.projectId ? '✅ Loaded' : '❌ Missing',
  storageBucket: firebaseConfig.storageBucket ? '✅ Loaded' : '❌ Missing',
  messagingSenderId: firebaseConfig.messagingSenderId ? '✅ Loaded' : '❌ Missing',
  appId: firebaseConfig.appId ? '✅ Loaded' : '❌ Missing',
  measurementId: firebaseConfig.measurementId ? '✅ Loaded' : '❌ Missing'
});

// ✅ Initialize Firebase services in the correct order - ensure single app instance
export const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

// 🛡️ Install auth hotfix immediately to prevent token refresh storms
installAuthHotfix();

// Log Firebase project for debugging
console.info("FIREBASE PROJECT:", app.options.projectId);

// 🛡️ Initialize App Check for security (only in production)
// let appCheck = null; // Unused - removed
if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
  try {
    // appCheck = initializeAppCheck(app, { // Unused - removed
    //   provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
    //   isTokenAutoRefreshEnabled: true,
    // });
    console.log('🛡️ App Check initialized for security');
  } catch (error) {
    console.warn('⚠️ App Check initialization failed:', error);
  }
}

export const auth = getAuth(app);
// ✅ Important: Set persistence to ensure session survives redirect
setPersistence(auth, browserLocalPersistence).catch(console.error);

export const db = getFirestore(app);
export const database = getDatabase(app);
export const storage = getStorage(app);
// Temporarily disabled to prevent background token/installation calls during admin lockout testing
// export const analytics = getAnalytics(app);
export const analytics = null;

// ✅ Important: match deployed region
export const functions = getFunctions(app, "us-central1");

// DEV ONLY: ensure no emulator connections in production
// if (import.meta?.env?.DEV) {
//   connectFunctionsEmulator(functions, "localhost", 5001);
//   // connectAuthEmulator(auth, "http://localhost:9099");
//   // connectFirestoreEmulator(db, "localhost", 8080);
// }

console.log('🚀 Firebase services initialized:', {
  auth: auth ? '✅' : '❌',
  db: db ? '✅' : '❌',
  database: database ? '✅' : '❌',
  storage: storage ? '✅' : '❌',
  analytics: analytics ? '✅' : '🚫 DISABLED',
  functions: functions ? '✅' : '❌'
});

// Helpful logs for debugging
console.info("AUTH APP NAME:", auth.app.name);

// Custom hook for authentication
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Fetch user data from Firestore
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          const userData = userDoc.data();
          
          // Combine Firebase Auth user with Firestore data
          const combinedUser = {
            ...firebaseUser,
            role: userData?.role || 'host',
            fullName: userData?.fullName || firebaseUser.displayName || firebaseUser.email?.split('@')[0],
            email: userData?.email || firebaseUser.email,
            // Add other Firestore fields as needed
            ...userData
          };
          
          setUser(combinedUser);
        } catch (error) {
          console.error('Error fetching user data from Firestore:', error);
          // Fallback to just Firebase Auth user
          setUser(firebaseUser);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return { user, loading };
};

// ✅ Export all required services
export { getAuth };
